import React from 'react'

import { Box, Stack } from '@mui/material'
import { AddressField, FieldLabel, Input, type ParsedAddress } from '@repo/ui'

import { SelectCountryField } from 'components/input/fields/SelectCountryField'

/**
 * The five fields that make up a postal address, as one reusable group.
 *
 * WHY THIS EXISTS. `CreateAddressModal` already composed exactly these fields
 * — `@repo/ui`'s `AddressField` for the autocompleting line, `SelectCountryField`
 * for the flagged country picker, and plain `Input`s for the rest — and the
 * signup payment step needed the same five for its billing address. That is the
 * SECOND hand-roll, which is where §36 says to extract rather than copy. The
 * alternative was a third address form drifting from the other two: the modal
 * has already been through one round of "make the country field the same height
 * as the name field beside it", and a copy would not have inherited that.
 *
 * WHAT IT DELIBERATELY DOES NOT OWN. No label, no type/icon picker, no
 * "set as default" checkbox, no submit. Those are the modal's concerns and not
 * a billing address's, so they stay with the caller. This group is only the
 * address itself.
 *
 * `CreateAddressModal` has NOT been migrated onto this yet — its layout pairs
 * Country with State, which is the one thing that genuinely differs (see the
 * ordering note below). Migrating it is a follow-up, not a silent side effect
 * of this change.
 */

/** The address this group edits. Every field is a string so the group can stay
 * uncontrolled-value-free; `null`-vs-`''` normalisation belongs to the caller
 * that persists it. */
export interface AddressFieldGroupValue {
  country: string
  address_line: string
  address_line_2: string
  city: string
  state: string
  postal_code: string
  /** Geocoding, filled only when the visitor picks an autocomplete suggestion.
   * Carried through so a caller that persists it (the Address Book feeds the
   * analytics scan map) does not have to re-geocode. */
  latitude: number | null
  longitude: number | null
  place_id: string | null
  formatted_address: string | null
}

export const EMPTY_ADDRESS_FIELD_GROUP_VALUE: AddressFieldGroupValue = {
  country: '',
  address_line: '',
  address_line_2: '',
  city: '',
  state: '',
  postal_code: '',
  latitude: null,
  longitude: null,
  place_id: null,
  formatted_address: null,
}

/** Which fields are missing. `state` is never required — plenty of countries
 * have no such subdivision, and demanding one there is how a form becomes
 * impossible to submit from Denmark. */
export type AddressFieldGroupErrors = Partial<
  Record<'country' | 'address_line' | 'city' | 'postal_code', boolean>
>

/**
 * The required-field check, exported so a caller can gate its own submit on
 * exactly what this group renders as invalid — rather than re-deriving the rule
 * and drifting from it.
 */
export const validateAddressFieldGroup = (
  value: AddressFieldGroupValue,
): AddressFieldGroupErrors => ({
  country: !value.country.trim(),
  address_line: !value.address_line.trim(),
  city: !value.city.trim(),
  postal_code: !value.postal_code.trim(),
})

export const hasAddressFieldGroupErrors = (errors: AddressFieldGroupErrors): boolean =>
  Object.values(errors).some(Boolean)

export interface AddressFieldGroupProps {
  value: AddressFieldGroupValue
  onChange: (next: AddressFieldGroupValue) => void
  /**
   * Google Maps key for the autocompleting address line. Without it
   * `AddressField` degrades to a plain text input, so manual entry still works
   * and the group stays usable — it just stops suggesting.
   */
  googleMapsApiKey?: string
  /** Show which required fields are empty. Off until the caller's first failed
   * submit, so an untouched form is not already red. */
  showErrors?: boolean
  disabled?: boolean
  /**
   * Country list for the picker, for callers with NO SESSION.
   *
   * `SelectCountryField` otherwise fetches `/common/countries`, which is
   * `AuthGuard`-gated — so on the public signup page the query 401s and the
   * picker stays permanently empty, with no error the visitor can act on.
   * Authenticated callers (the Address Book modal) omit this and let it fetch.
   */
  countries?: { code: string; name: string }[]
}

export const AddressFieldGroup = ({
  value,
  onChange,
  googleMapsApiKey,
  showErrors = false,
  disabled,
  countries,
}: AddressFieldGroupProps) => {
  const errors = showErrors ? validateAddressFieldGroup(value) : {}

  const set = <K extends keyof AddressFieldGroupValue>(
    key: K,
    next: AddressFieldGroupValue[K],
  ) => onChange({ ...value, [key]: next })

  // A picked suggestion fills the structured fields AND the geocoding. It
  // overwrites rather than merges: the visitor chose this specific place, so
  // leaving a previously-typed city from a different one would be worse than
  // replacing it. Country is included because a suggestion can legitimately be
  // in a different country than the one selected — only possible when no
  // country is set, since the query is scoped below when one is.
  const applyParsed = (parsed: ParsedAddress) =>
    onChange({
      ...value,
      address_line: parsed.address_line || value.address_line,
      city: parsed.city || value.city,
      state: parsed.state ?? value.state,
      country: parsed.country || value.country,
      postal_code: parsed.postal_code || value.postal_code,
      latitude: parsed.latitude,
      longitude: parsed.longitude,
      place_id: parsed.place_id,
      formatted_address: parsed.formatted_address,
    })

  return (
    <Stack spacing={4}>
      {/*
        COUNTRY FIRST, and this is an ordering decision rather than an aesthetic
        one: the country SCOPES the autocomplete query below it (`countryCode`).
        Asking for it after the address line means the first search runs
        worldwide, and picking a suggestion from it then rewrites the country
        the visitor is about to choose — the form appearing to argue with them.
      */}
      <SelectCountryField
        label="Country"
        value={value.country}
        onChange={code => set('country', code)}
        countriesOverride={countries}
        disabled={disabled}
        error={errors.country ? { type: 'required', message: 'Select a country' } : undefined}
      />

      {/* `AddressField` has no `label` of its own, so the shared `FieldLabel`
          supplies it — NOT a hand-rolled Typography, which is what §31 exists to
          stop: the label→control gap is FieldLabel's to own, and a local copy
          would drift from every other field on this form the moment it moves. */}
      <Box>
        <FieldLabel label="Address" />
        <AddressField
          apiKey={googleMapsApiKey}
          countryCode={value.country || null}
          defaultValue={value.address_line}
          onSelect={applyParsed}
          onInputChange={v => set('address_line', v)}
          error={errors.address_line}
          disabled={disabled}
        />
      </Box>

      <Input
        label="Apartment, suite, etc."
        placeholder="Optional"
        value={value.address_line_2}
        onValueChange={v => set('address_line_2', v)}
        disabled={disabled}
      />

      {/* City -> State -> Postal code, in the order the address is SPOKEN and
          written on an envelope. State used to sit last, on its own full-width
          row below a City/Postal pair, which put it out of sequence and gave a
          minor optional field more visual weight than the two required ones
          above it. It is still never required (see AddressFieldGroupErrors) —
          only its position changed.

          Three across rather than a 2+1: the row now reads as one address line,
          and State is the narrowest because "CA" needs least room. `flex` on
          the wrappers rather than a Stack of equal children, since equal thirds
          would give a two-letter state the same width as a city name. */}
      <Stack direction="row" spacing={4} alignItems="flex-start">
        <Box sx={{ flex: 2, minWidth: 0 }}>
          <Input
            label="City"
            placeholder="Enter"
            value={value.city}
            onValueChange={v => set('city', v)}
            error={errors.city}
            disabled={disabled}
            fullWidth
          />
        </Box>
        <Box sx={{ flex: 1.4, minWidth: 0 }}>
          <Input
            label="State / Province"
            placeholder="Optional"
            value={value.state}
            onValueChange={v => set('state', v)}
            disabled={disabled}
            fullWidth
          />
        </Box>
        <Box sx={{ flex: 1.2, minWidth: 0 }}>
          <Input
            label="Postal code"
            placeholder="Enter"
            value={value.postal_code}
            onValueChange={v => set('postal_code', v)}
            error={errors.postal_code}
            disabled={disabled}
            fullWidth
          />
        </Box>
      </Stack>
    </Stack>
  )
}
